import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { GraduationCap, Search, Mail, User, LogOut, Home, Menu, X, ChevronLeft, ChevronRight, Sparkles, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Profile } from '../lib/types';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const mainRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    
    return () => {};
  }, [location.pathname]);

  const isActive = (path: string) => location.pathname === path;

  useEffect(() => {
    let mounted = true;
    
    const loadUserData = async () => {
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          navigate('/login');
          return;
        }
        
        const { data: paidUser } = await supabase
          .from('paid_users')
          .select()
          .eq('email', user?.email);
        
        if (!paidUser || paidUser.length === 0) {
          navigate('/checkout', { 
            state: { 
              customer_email: user.email,
              redirected_from: 'dashboard'
            } 
          });
          return;
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        if (mounted) {
          navigate('/login');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    loadUserData();
    
    return () => {
      mounted = false;
    };
  }, [navigate]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="relative w-12 h-12">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-12 w-12 rounded-full border-t-2 border-b-2 border-indigo-500 animate-spin"></div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-indigo-500" />
          </div>
        </div>
      </div>
    );
  }

  const navigationItems = [
    { path: '/dashboard', icon: Home, label: 'Dashboard' },
    { path: '/search', icon: Search, label: 'Search Professors' },
    { path: '/emails', icon: Mail, label: 'Email Generator' },
    { path: '/replies', icon: MessageCircle, label: 'Professor Replies' },
  ];

  const sidebarVariants = {
    expanded: { width: '240px' },
    collapsed: { width: '72px' }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-900 text-gray-100">
      <div className="flex flex-1">
        {/* Desktop sidebar */}
        <motion.aside 
          className="hidden md:flex flex-col bg-gray-800 sticky top-0 h-screen"
          initial={isSidebarCollapsed ? "collapsed" : "expanded"}
          animate={isSidebarCollapsed ? "collapsed" : "expanded"}
          variants={sidebarVariants}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          <motion.button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="absolute -right-3 top-20 transform -translate-y-1/2 bg-gray-800 rounded-full p-1.5 hover:bg-gray-700 z-10 text-gray-300"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            {isSidebarCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronLeft className="h-4 w-4" />
            )}
          </motion.button>
          
          <nav className="mt-8 px-2 space-y-1 flex-1 overflow-y-auto">
            {navigationItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`group flex items-center px-3 py-2.5 text-base font-medium rounded-lg transition-all duration-200 ${
                  isActive(item.path)
                    ? 'bg-gradient-to-r from-indigo-800 to-purple-800 text-white'
                    : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                } ${isSidebarCollapsed ? 'justify-center' : ''}`}
              >
                <motion.div 
                  whileHover={{ scale: isActive(item.path) ? 1 : 1.1 }}
                  className={`${isActive(item.path) ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}
                >
                  <item.icon className={`h-5 w-5 flex-shrink-0 ${isSidebarCollapsed ? '' : 'mr-3'}`} />
                </motion.div>
                {!isSidebarCollapsed && (
                  <span>{item.label}</span>
                )}
                {isActive(item.path) && !isSidebarCollapsed && (
                  <motion.div 
                    className="absolute left-0 w-1 h-6 bg-indigo-400 rounded-r-md"
                    layoutId="activeIndicator"
                  />
                )}
              </Link>
            ))}
            <motion.button
              onClick={handleSignOut}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full group flex items-center px-3 py-2.5 text-base font-medium rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200 ${
                isSidebarCollapsed ? 'justify-center' : ''
              }`}
            >
              <LogOut className={`h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-white ${isSidebarCollapsed ? '' : 'mr-3'}`} />
              {!isSidebarCollapsed && 'Sign Out'}
            </motion.button>
          </nav>
        </motion.aside>

        {/* Mobile menu */}
        <div className="md:hidden">
          <motion.button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="fixed top-4 left-4 z-50 p-2 rounded-lg bg-gray-800 text-gray-300 border border-gray-700"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </motion.button>
        </div>

        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div 
                className="fixed inset-0 z-40 bg-black bg-opacity-80 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <motion.div 
                className="fixed inset-y-0 left-0 w-64 bg-gray-800 shadow-xl z-50 pt-16"
                initial={{ x: "-100%" }}
                animate={{ x: 0 }}
                exit={{ x: "-100%" }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
              >
                <nav className="mt-4 px-2 space-y-1">
                  {navigationItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`group flex items-center px-3 py-2.5 text-base font-medium rounded-lg transition-all duration-200 ${
                        isActive(item.path)
                          ? 'bg-gradient-to-r from-indigo-800 to-purple-800 text-white'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.div whileHover={{ scale: 1.1 }} className="text-gray-400 group-hover:text-white">
                        <item.icon className="h-5 w-5 flex-shrink-0 mr-3" />
                      </motion.div>
                      <span>{item.label}</span>
                      {isActive(item.path) && (
                        <motion.div 
                          className="absolute left-0 w-1 h-6 bg-indigo-400 rounded-r-md"
                          layoutId="mobileActiveIndicator"
                        />
                      )}
                    </Link>
                  ))}
                  <motion.button
                    onClick={handleSignOut}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full group flex items-center px-3 py-2.5 text-base font-medium rounded-lg text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
                  >
                    <LogOut className="h-5 w-5 flex-shrink-0 mr-3 text-gray-400 group-hover:text-white" />
                    Sign Out
                  </motion.button>
                </nav>
              </motion.div>
            </>
          )}
        </AnimatePresence>

        <main
          ref={mainRef}
          className="flex-1 px-4 py-6 sm:px-6 lg:px-8 overflow-y-auto overflow-x-hidden bg-gray-900"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
}